import {inject, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {SharedDataService} from '../shared-data.service';
import {TranslateService} from '@ngx-translate/core';
import {interval, take} from 'rxjs';
import {JwtService} from '../auth/jwt.service';
import {SERVER_API_URL} from '../../shared/utils.constants';
import {UsersService} from './users.service';
import {MessageService} from 'primeng/api';
import {PrimeNG} from "primeng/config";

@Injectable({ providedIn: 'root' })
export class LoginService {
    jwt: JwtService = inject(JwtService);
    http: HttpClient = inject(HttpClient);
    router: Router = inject(Router);
    sharedDataService: SharedDataService = inject(SharedDataService);
    translateService: TranslateService = inject(TranslateService);
    usersService: UsersService = inject(UsersService);
    messageService: MessageService = inject(MessageService);
    primeng: PrimeNG = inject(PrimeNG);

    sessionId = signal<string | null>(null);

    login(email: string, password: string) {
        const payload = { email: email, password: password };
        this.http.post<any>(`${SERVER_API_URL}login`, payload, {
            observe: 'body',
            responseType: 'json'
        }).pipe(take(1)).subscribe({
            next: (res) => {
                const token = res?.token ?? res?.accessToken;
                if (!token) {
                    return;
                }

                // If authenticator is activated
                if (res?.user?.isAuthenticator) {
                    this.verifyAuthenticator(email, res);
                } else {
                    this.initUser(res);
                }
            }, error: (err) => {
                console.error(err);
            }
        });
    }

    logout(): void {
        // Reset data
        this.sharedDataService.user.set(null);
        this.translateService.use('fr')
        this.jwt.removeSession();
    }

    verifyAuthenticator(username: string, loginRes: any): void {
        this.usersService.startAuthenticatorCountdown(40);
        const userData = this.usersService.getUserDataForAuthenticator(username);
        this.http.get(this.usersService.authenticatorUrl  + 'session/' + userData).pipe(take(1)).subscribe((data: any) => {
            this.pollSessionStatus(data.sessionId, loginRes);
        });
    }

    private pollSessionStatus(sessionId: any, loginRes: any): void {
        let elapsed = 0;
        this.sessionId.set(sessionId);
        const polling$ = interval(2000).subscribe(() => {
            elapsed += 2;
            if (elapsed > 40) {
                polling$.unsubscribe();
                this.sessionId.set(null);
                return;
            }

            this.http.post<any>(this.usersService.authenticatorUrl + 'session/status', { sessionId }).pipe(take(1)).subscribe({
                next: (res) => {
                    if (res.status === 'accepted') {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: this.translateService.instant('authenticator.activation_success'),
                        });
                        polling$.unsubscribe();
                        this.usersService.countdown.set(0);
                        this.sessionId.set(null);

                        this.initUser(loginRes);
                    } else if (res.status === 'rejected') {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: this.translateService.instant('authenticator.activation_denied'),
                        });
                        polling$.unsubscribe();
                        this.usersService.countdown.set(0);
                        this.sessionId.set(null);
                        this.sharedDataService.blockLayout.set(false);
                    }
                },
                error: () => {}
            });
        });
    }

    /**
     * Store user info
     * @param loginRes
     */
    initUser(loginRes: any): void {
        const token = loginRes?.token ?? loginRes?.accessToken;

        this.sharedDataService.user.set(loginRes?.user);
        this.sharedDataService.menuList.set(this.sharedDataService.model());
        this.translateService.use(this.sharedDataService.user()?.lang || 'fr');
        this.translateService.get('primeng').subscribe(res => this.primeng.setTranslation(res));
        this.jwt.setSession(token, loginRes?.expiresAt);

        this.router.navigate(['/']);
    }
}
